/**
 * TasksQuickSheet - Painel lateral premium "Minhas tarefas"
 *
 * Adota a hierarquia visual do inspetor de Fluxos: cabeçalho compacto,
 * navegação destacada, conteúdo rolável e ações fixas. Lista as tarefas do
 * usuário por situação e reaproveita o cronômetro e a ficha oficiais.
 *
 * @version 1.5.0
 * RELEASE: 2026-10-04 v1.5.0 - Grupos de status retráteis/expansíveis e campo
 *            de busca por título/número dentro do painel.
 * RELEASE: 2026-10-04 v1.4.0 - Reutiliza o diálogo oficial de motivo ao pausar pelo painel.
 * RELEASE: 2026-10-04 v1.3.0 - Alinha o cabeçalho ao Workflow e corrige o contexto da ficha.
 * RELEASE: 2026-10-04 v1.2.0 - Adiciona fixação no cronômetro, corrige a lista e remove o FAB.
 * RELEASE: 2026-10-04 v1.1.0 - Redesenho no padrão de Fluxos, abas visuais,
 *            prioridade, prazo restante e alternância entre cabeçalho/FAB.
 * RELEASE: 2026-10-04 v1.0.0 - Criação inicial.
 */
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { differenceInMinutes, format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { BriefcaseBusiness, CalendarClock, ChevronDown, Clock3, Gavel, ListChecks, Pin, Search, X } from 'lucide-react';
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Button } from '@/components/ui/button';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { Skeleton } from '@/components/ui/skeleton';
import { TaskTimerCell } from '@/components/tasks/TaskTimerCell';
import { TaskDetailsModal } from '@/components/tasks/modal/TaskDetailsModal';
import { TaskPauseReasonDialog } from '@/components/tasks/modal/TaskPauseReasonDialog';
import { PRIORITY_LABELS, PriorityFlag } from '@/components/tasks/PriorityFlag';
import { TaskModuleProvider } from '@/contexts/TaskModuleContext';
import { PermissionsProvider } from '@/contexts/PermissionsContext';
import { useMyActiveTasks } from '@/hooks/useMyActiveTasks';
import { useTaskPauses } from '@/hooks/useTaskPauses';
import { useTasksModuleSettings } from '@/hooks/useTasksModuleSettings';
import type { TaskModuleType } from '@/hooks/useTaskModuleType';
import type { TaskListItem } from '@/hooks/useTasksPaginated';
import { cn } from '@/lib/utils';

/** Grupos exibidos, na ordem operacional. */
const GROUPS: { key: string; label: string; dot: string }[] = [
  { key: 'in_progress', label: 'Em andamento', dot: 'bg-primary' },
  { key: 'paused', label: 'Pausadas', dot: 'bg-warning' },
  { key: 'pending', label: 'Pendentes', dot: 'bg-muted-foreground' },
];

/** Rotas oficiais de listagem por módulo. */
const LIST_PATH: Record<TaskModuleType, string> = {
  normal: '/tenant/tasks/list',
  retention: '/tenant/retention',
  legal: '/tenant/legal',
};

/** Ícones usados na navegação entre módulos. */
const MODULE_ICON: Record<TaskModuleType, typeof ListChecks> = {
  normal: ListChecks,
  retention: BriefcaseBusiness,
  legal: Gavel,
};

/** Agrupa pela situação; tarefa com pausa ativa pertence ao grupo Pausadas. */
function groupOf(task: TaskListItem): string {
  if (task.is_paused || task.status === 'paused') return 'paused';
  return task.status;
}

/** Normaliza texto para a busca: remove acentos, baixa o caixa e remove espaços extras. */
function normalizeText(value: string): string {
  return value.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase().trim();
}

/** Converte a diferença de prazo em uma informação curta e gerencial. */
function getDeadlineInfo(dueDate: string | null, status: string) {
  if (!dueDate || status === 'completed' || status === 'cancelled') return null;
  const diffMinutes = differenceInMinutes(new Date(dueDate), new Date());
  const absoluteMinutes = Math.abs(diffMinutes);
  const days = Math.floor(absoluteMinutes / 1440);
  const hours = Math.floor((absoluteMinutes % 1440) / 60);
  const minutes = absoluteMinutes % 60;
  const compact = days > 0 ? `${days}d ${hours}h` : hours > 0 ? `${hours}h ${minutes}m` : `${minutes}m`;

  return diffMinutes < 0
    ? { label: `Vencida há ${compact}`, overdue: true }
    : { label: `Faltam ${compact}`, overdue: false };
}

/** Cartão compacto com informações essenciais e controles oficiais da tarefa. */
function QuickTaskCard({ task, type, onOpenTask, onPauseRequest, selectedTaskId, onSelectTask }: {
  task: TaskListItem;
  type: TaskModuleType;
  onOpenTask: (id: string, type: TaskModuleType) => void;
  onPauseRequest: (taskId: string) => void;
  selectedTaskId: string | null;
  onSelectTask: (taskId: string | null) => void;
}) {
  const deadline = getDeadlineInfo(task.due_date, task.status);
  const priorityLabel = PRIORITY_LABELS[task.priority] ?? PRIORITY_LABELS.normal;

  const isSelected = task.id === selectedTaskId;

  return (
    <li className="group overflow-hidden rounded-lg border border-border bg-card shadow-sm transition-all duration-200 hover:-translate-y-0.5 hover:border-primary/30 hover:shadow-md">
      <button
        type="button"
        onClick={() => onOpenTask(task.id, type)}
        className="block w-full px-3.5 pb-2.5 pt-3 text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring"
        title={task.title}
      >
        <span className="mb-1 block text-[11px] font-medium text-muted-foreground">#{task.number}</span>
        <span className="line-clamp-2 text-sm font-semibold leading-5 text-foreground transition-colors group-hover:text-primary">
          {task.title || 'Sem título'}
        </span>
      </button>

      <div className="mx-3.5 flex flex-wrap items-center gap-x-3 gap-y-1.5 border-t border-border/70 py-2.5 text-xs">
        <span className="inline-flex items-center gap-1.5 font-medium text-foreground">
          <PriorityFlag priority={task.priority} size={14} />
          {priorityLabel}
        </span>
        <span className="inline-flex items-center gap-1.5 text-muted-foreground">
          <CalendarClock className="h-3.5 w-3.5" />
          {task.due_date
            ? format(new Date(task.due_date), "dd/MM/yyyy 'às' HH:mm", { locale: ptBR })
            : 'Sem prazo'}
        </span>
      </div>

      <div className="flex min-h-10 items-center justify-between gap-3 bg-muted/35 px-3.5 py-2">
        <span className={cn(
          'inline-flex min-w-0 items-center gap-1.5 text-xs font-medium',
          deadline?.overdue ? 'text-destructive' : 'text-muted-foreground',
        )}>
          <Clock3 className="h-3.5 w-3.5 shrink-0" />
          {deadline?.label ?? 'Prazo não definido'}
        </span>
        <div className="flex items-center gap-1.5">
          {groupOf(task) === 'in_progress' && onSelectTask && (
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  type="button"
                  variant="ghost"
                  size="icon-sm"
                  className={cn('rounded-full', isSelected && 'bg-primary/10 text-primary')}
                  aria-label={isSelected ? 'Remover tarefa do cronômetro' : 'Fixar tarefa no cronômetro'}
                  onClick={() => onSelectTask(isSelected ? null : task.id)}
                >
                  <Pin className={cn('h-3.5 w-3.5', isSelected && 'fill-current')} />
                </Button>
              </TooltipTrigger>
              <TooltipContent side="left">
                {isSelected ? 'Remover do cronômetro' : 'Fixar no cronômetro'}
              </TooltipContent>
            </Tooltip>
          )}
          <TaskTimerCell
            taskId={task.id}
            status={task.status}
            startedAt={task.started_at}
            isPaused={task.is_paused}
            totalPauseDurationSeconds={task.total_pause_duration_seconds}
            currentPauseStartedAt={task.current_pause_started_at}
            canChangeStatus
            onPauseRequest={() => onPauseRequest(task.id)}
            completedAt={task.completed_at}
            cancelledAt={task.cancelled_at}
            dueDate={task.due_date}
          />
        </div>
      </div>
    </li>
  );
}

/** Lista agrupada de um módulo, com grupos retráteis e busca por título/número. */
function ModuleTaskList({ type, onOpenTask, onPauseRequest, onSeeAll, selectedTaskId, onSelectTask, searchTerm, collapsedGroups, onToggleGroup }: {
  type: TaskModuleType;
  onOpenTask: (id: string, type: TaskModuleType) => void;
  onPauseRequest: (taskId: string) => void;
  onSeeAll: () => void;
  selectedTaskId: string | null;
  onSelectTask: (taskId: string | null) => void;
  /** Termo de busca do painel (vazio desativa o filtro). */
  searchTerm: string;
  /** Estado de colapso compartilhado por módulo: chave `${módulo}:${grupo}`. */
  collapsedGroups: Record<string, boolean>;
  /** Alterna o colapso de um grupo do módulo atual. */
  onToggleGroup: (groupKey: string) => void;
}) {
  const { data = [], isLoading } = useMyActiveTasks(type);

  if (isLoading) {
    return <div className="space-y-3">{[0, 1, 2].map((item) => <Skeleton key={item} className="h-36 w-full rounded-lg" />)}</div>;
  }

  // Busca client-side sobre o lote já carregado pelo atalho (título e número).
  const normalizedSearch = normalizeText(searchTerm);
  const filteredData = normalizedSearch
    ? data.filter((task) => (
      normalizeText(task.title ?? '').includes(normalizedSearch)
      || String(task.number ?? '').includes(normalizedSearch)
    ))
    : data;
  const isSearching = normalizedSearch.length > 0;
  const hasResults = !isSearching || filteredData.length > 0;

  return (
    <div className="space-y-6">
      {hasResults ? (
        <>
          {GROUPS.map((group) => {
            const items = filteredData.filter((task) => groupOf(task) === group.key);
            // Durante a busca, grupos sem correspondência são ocultados.
            if (isSearching && items.length === 0) return null;

            const groupStateKey = `${type}:${group.key}`;
            // Com busca ativa todos os grupos ficam expandidos para exibir os resultados.
            const isCollapsed = !isSearching && (collapsedGroups[groupStateKey] ?? false);

            return (
              <section key={group.key} aria-labelledby={`tasks-quick-${type}-${group.key}`}>
                {/* Cabeçalho clicável: alterna o colapso do grupo mantendo a contagem visível. */}
                <button
                  type="button"
                  id={`tasks-quick-${type}-${group.key}`}
                  onClick={() => onToggleGroup(group.key)}
                  aria-expanded={!isCollapsed}
                  className="mb-2.5 flex w-full items-center gap-2 rounded-md text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                >
                  <span className={cn('h-2 w-2 shrink-0 rounded-full', group.dot)} />
                  <span className="text-xs font-semibold uppercase text-foreground">
                    {group.label}
                  </span>
                  <span className="rounded-full bg-muted px-2 py-0.5 text-[11px] font-semibold text-muted-foreground">
                    {items.length}
                  </span>
                  <ChevronDown
                    className={cn(
                      'ml-auto h-4 w-4 text-muted-foreground transition-transform duration-200',
                      isCollapsed && '-rotate-90',
                    )}
                  />
                </button>

                {!isCollapsed && (
                  items.length === 0 ? (
                    <div className="rounded-lg border border-dashed border-border px-3 py-4 text-center text-xs text-muted-foreground">
                      Nenhuma tarefa neste grupo.
                    </div>
                  ) : (
                    <ul className="space-y-2.5">
                      {items.map((task) => (
                        <QuickTaskCard
                          key={task.id}
                          task={task}
                          type={type}
                          onOpenTask={onOpenTask}
                          onPauseRequest={onPauseRequest}
                          selectedTaskId={selectedTaskId}
                          onSelectTask={onSelectTask}
                        />
                      ))}
                    </ul>
                  )
                )}
              </section>
            );
          })}
          <Button variant="outline" size="sm" className="w-full" onClick={onSeeAll}>
            Ver todas as tarefas
          </Button>
        </>
      ) : (
        <div className="rounded-lg border border-dashed border-border px-4 py-6 text-center text-sm text-muted-foreground">
          Nenhuma tarefa encontrada para “{searchTerm.trim()}”.
        </div>
      )}
    </div>
  );
}

interface TasksQuickSheetProps {
  open: boolean;
  onOpenChange: (value: boolean) => void;
  selectedTaskId: string | null;
  onSelectTask: (taskId: string | null) => void;
}

export function TasksQuickSheet({ open, onOpenChange, selectedTaskId, onSelectTask }: TasksQuickSheetProps) {
  const navigate = useNavigate();
  const { retentionEnabled, legalEnabled } = useTasksModuleSettings();
  const [tab, setTab] = useState<TaskModuleType>('normal');
  const [viewTask, setViewTask] = useState<{ id: string; type: TaskModuleType } | null>(null);
  /** Termo de busca ativo; limpo ao trocar de módulo para não misturar contextos. */
  const [searchTerm, setSearchTerm] = useState('');
  /** Estado de colapso dos grupos, mantido por módulo: chave `${módulo}:${grupo}`. */
  const [collapsedGroups, setCollapsedGroups] = useState<Record<string, boolean>>({});
  /** Tarefa aguardando a confirmação da pausa com motivo opcional. */
  const [pauseTaskId, setPauseTaskId] = useState<string | null>(null);
  const { pauseTask, isPausing } = useTaskPauses(pauseTaskId, false);

  /** Alterna o colapso de um grupo da aba atual, preservando os demais módulos. */
  const toggleGroup = (groupKey: string) => {
    const stateKey = `${tab}:${groupKey}`;
    setCollapsedGroups((prev) => ({ ...prev, [stateKey]: !(prev[stateKey] ?? false) }));
  };

  const tabs: { key: TaskModuleType; label: string }[] = [
    { key: 'normal', label: 'Tarefas' },
    ...(retentionEnabled ? [{ key: 'retention' as const, label: 'Retenção' }] : []),
    ...(legalEnabled ? [{ key: 'legal' as const, label: 'Jurídico' }] : []),
  ];

  return (
    <>
      <Sheet open={open} onOpenChange={onOpenChange}>
        <SheetContent side="right" className="flex w-full flex-col gap-0 p-0 sm:inset-y-3 sm:right-3 sm:h-auto sm:max-w-[480px] sm:rounded-xl sm:border sm:shadow-xl">
          <SheetHeader className="border-b px-4 py-3 pr-14">
            <div className="flex items-start gap-2 text-left">
              {/* Replica a escala compacta do cabeçalho oficial do inspetor de Workflow. */}
              <span className="mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-lg bg-muted text-primary">
                <ListChecks className="h-4 w-4" />
              </span>
              <div className="min-w-0 flex-1">
                <SheetTitle className="truncate text-sm font-semibold text-foreground">Minhas tarefas</SheetTitle>
                <SheetDescription className="truncate text-[11px] uppercase text-muted-foreground">
                  Tarefas em execução
                </SheetDescription>
              </div>
            </div>
          </SheetHeader>

          <Tabs
            value={tab}
            onValueChange={(value) => {
              setTab(value as TaskModuleType);
              setSearchTerm('');
            }}
            className="flex min-h-0 flex-1 flex-col"
          >
            {tabs.length > 1 && (
              <div className="border-b border-border px-5 pt-3">
                <TabsList className="h-9 w-full justify-start gap-1 bg-muted/50 p-1">
                  {tabs.map((item) => {
                    const Icon = MODULE_ICON[item.key];
                    return (
                      <TabsTrigger
                        key={item.key}
                        value={item.key}
                        className="relative h-7 flex-1 gap-1.5 rounded-md px-2 text-xs data-[state=active]:bg-background data-[state=active]:shadow-sm after:absolute after:-bottom-1 after:left-1/2 after:h-0.5 after:w-7 after:-translate-x-1/2 after:rounded-full after:bg-primary after:opacity-0 data-[state=active]:after:opacity-100"
                      >
                        <Icon className="h-3.5 w-3.5" />
                        {item.label}
                      </TabsTrigger>
                    );
                  })}
                </TabsList>
              </div>
            )}

            {/* Campo de busca do painel: localiza tarefas por título ou número no módulo ativo. */}
            <div className="border-b border-border px-5 py-3">
              <div className="relative">
                <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                <input
                  type="text"
                  value={searchTerm}
                  onChange={(event) => setSearchTerm(event.target.value)}
                  placeholder="Buscar tarefa por título ou nº…"
                  maxLength={120}
                  aria-label="Buscar tarefa no painel Minhas tarefas"
                  className="h-9 w-full rounded-lg border border-border bg-background pl-9 pr-9 text-sm text-foreground shadow-sm outline-none transition-all placeholder:text-muted-foreground/70 focus-visible:border-primary/40 focus-visible:ring-2 focus-visible:ring-ring/25"
                />
                {searchTerm && (
                  <button
                    type="button"
                    onClick={() => setSearchTerm('')}
                    aria-label="Limpar busca"
                    className="absolute right-2 top-1/2 flex h-6 w-6 -translate-y-1/2 items-center justify-center rounded-full text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
                  >
                    <X className="h-3.5 w-3.5" />
                  </button>
                )}
              </div>
            </div>

            {tabs.map((item) => (
              <TabsContent key={item.key} value={item.key} className="mt-0 min-h-0 flex-1 overflow-y-auto bg-muted/20 px-5 py-5">
                {/* O provider preserva as regras e invalidações próprias de cada módulo. */}
                <TaskModuleProvider type={item.key}>
                  <ModuleTaskList
                    type={item.key}
                    onOpenTask={(id, type) => {
                      // Fecha o painel antes de montar a ficha para evitar camadas concorrentes.
                      onOpenChange(false);
                      setViewTask({ id, type });
                    }}
                    onPauseRequest={setPauseTaskId}
                    selectedTaskId={selectedTaskId}
                    onSelectTask={onSelectTask}
                    searchTerm={searchTerm}
                    collapsedGroups={collapsedGroups}
                    onToggleGroup={toggleGroup}
                    onSeeAll={() => {
                      onOpenChange(false);
                      navigate(LIST_PATH[item.key]);
                    }}
                  />
                </TaskModuleProvider>
              </TabsContent>
            ))}
          </Tabs>

        </SheetContent>
      </Sheet>

      {/* Uma única instância atende todos os cartões e preserva o painel aberto. */}
      <TaskPauseReasonDialog
        open={pauseTaskId !== null}
        onOpenChange={(value) => {
          if (!value && !isPausing) setPauseTaskId(null);
        }}
        onConfirm={async (reason) => {
          await pauseTask({ reason });
          setPauseTaskId(null);
        }}
        isLoading={isPausing}
      />

      {viewTask && (
        /* O atalho vive acima das rotas protegidas; a ficha precisa do mesmo
           contexto de permissões usado pelos demais modais globais. */
        <PermissionsProvider>
          <TaskModuleProvider type={viewTask.type}>
            <TaskDetailsModal taskId={viewTask.id} open onOpenChange={(value) => { if (!value) setViewTask(null); }} />
          </TaskModuleProvider>
        </PermissionsProvider>
      )}
    </>
  );
}
